import React, { useEffect, useReducer, useRef, useState } from "react";
import cn from "classnames";
import * as Sentry from "@sentry/react";
import { defineMessages, useIntl } from "react-intl";
import { OverlayTriggerState } from "react-stately";
import { Step, stepReducer } from "./utils";
import { TeacherDuoItem } from "../../../Duo.types";
import {
    Dialog,
    ModalPortal,
} from "@evidenceb/athena-common/design-system/Modal";
import { useBreakpoints } from "@evidenceb/athena-common/helpers/breakpoints";
import ChooseGroup from "./ChooseGroup/ChooseGroup";
import ChooseDuo from "./ChooseDuo/ChooseDuo";
import { commonMessages } from "../../../../../../utils/messages";
import DefineRoles from "./DefineRoles/DefineRoles";
import StepProgression from "./StepProgression/StepProgression";
import { useParams } from "react-router-dom";
import useTeacherDuo from "../../useTeacherDuo";
import { Button } from "@evidenceb/athena-common/design-system/Button";
import useOnlineStatus from "../../../../../../hooks/useOnlineStatus";

import "./AddDuoModal.scss";

interface Props {
    state: OverlayTriggerState;
}

export type DuoConstruction = Partial<
    Pick<TeacherDuoItem, "group" | "mentee" | "mentor">
>;

const AddDuoModal = ({ state }: Props) => {
    const intl = useIntl();
    const isOnline = useOnlineStatus();
    const { isTabletOrSmaller } = useBreakpoints();
    const { createDuo } = useTeacherDuo();
    const { resourceId } = useParams<{ resourceId: string }>();

    const [step, setStep] = useReducer(stepReducer, Step.ChooseGroup);
    const [duo, setDuo] = useState<DuoConstruction>({});
    const [error, setError] = useState<boolean>(false);
    const [showDialog, setShowDialog] = useState<boolean>(true);

    const handleSubmit = async () => {
        try {
            await createDuo(duo, resourceId);
            setShowDialog(false);
        } catch (err) {
            Sentry.captureException(err);
            setError(true);
        }
    };

    const primaryBtnRef = useRef<HTMLButtonElement>(null);
    const [firstDuoSet, setFirstDuoSet] = useState<boolean>(false);
    // Put focus on next button when first duo is chosen
    useEffect(() => {
        if (firstDuoSet || !duo.mentor || !duo.mentee || !primaryBtnRef.current)
            return;
        setFirstDuoSet(true);
        primaryBtnRef.current.focus();
    }, [duo.mentee, duo.mentor, firstDuoSet]);

    return (
        <ModalPortal state={state} variant="attach-to-bottom-on-mobile">
            <Dialog
                showDialog={showDialog}
                className="add-duo-modal"
                variant="attach-to-bottom-on-mobile"
                onClose={state.close}
                title={{
                    as: "div",
                    content: (
                        <div className="add-duo-modal__title">
                            <h3>
                                {intl.formatMessage(messages.addDuoModalTitle)}
                            </h3>
                            {isTabletOrSmaller && (
                                <button
                                    onClick={() => {
                                        setShowDialog(false);
                                    }}
                                >
                                    {intl.formatMessage(
                                        messages.addDuoModalCancel
                                    )}
                                </button>
                            )}
                        </div>
                    ),
                }}
                headerClassName="add-duo-modal__header"
                headerChildren={<StepProgression step={step} />}
                contentClassName="add-duo-modal__content"
            >
                <div className="add-duo-modal__body">
                    {step === Step.ChooseGroup && (
                        <ChooseGroup setDuo={setDuo} setStep={setStep} />
                    )}
                    {step === Step.ChooseDuo && duo.group && (
                        <ChooseDuo
                            group={duo.group}
                            setDuo={setDuo}
                            mentee={duo.mentee}
                            mentor={duo.mentor}
                        />
                    )}
                    {step === Step.DefineRoles && (
                        <DefineRoles duo={duo} setDuo={setDuo} />
                    )}
                </div>
                {(!isTabletOrSmaller ||
                    (isTabletOrSmaller && step !== Step.ChooseGroup)) && (
                    <footer>
                        {error && (
                            <p className="add-duo-modal__error">
                                {intl.formatMessage(messages.error)}
                            </p>
                        )}

                        <div className="add-duo-modal__buttons-container">
                            {!isTabletOrSmaller && (
                                <button
                                    className="cancel-button"
                                    onClick={() => {
                                        setShowDialog(false);
                                    }}
                                >
                                    {intl.formatMessage(
                                        messages.addDuoModalCancel
                                    )}
                                </button>
                            )}

                            {step !== Step.ChooseGroup && (
                                <div className="step-buttons">
                                    <Button
                                        variant="secondary"
                                        color="primary"
                                        label={intl.formatMessage(
                                            commonMessages.previous
                                        )}
                                        onPress={() => {
                                            setStep({ type: "previousStep" });
                                        }}
                                    />
                                    {step === Step.ChooseDuo && (
                                        <Button
                                            ref={primaryBtnRef}
                                            variant="custom"
                                            colors={{
                                                background:
                                                    "var(--element-teacher-default)",
                                                text: "var(--text-main-inverted)",
                                                hoverBackground:
                                                    "var(--element-button-custom-hover)",
                                            }}
                                            label={intl.formatMessage(
                                                commonMessages.next
                                            )}
                                            onPress={() => {
                                                setStep({ type: "nextStep" });
                                            }}
                                            disabled={
                                                !duo.mentor || !duo.mentee
                                            }
                                        />
                                    )}
                                    {step === Step.DefineRoles && (
                                        <Button
                                            variant="custom"
                                            colors={{
                                                background:
                                                    "var(--element-teacher-default)",
                                                text: "var(--text-main-inverted)",
                                                hoverBackground:
                                                    "var(--element-button-custom-hover)",
                                            }}
                                            className={cn({
                                                "step-button--error": error,
                                            })}
                                            label={
                                                error
                                                    ? intl.formatMessage(
                                                          messages.retry
                                                      )
                                                    : intl.formatMessage(
                                                          commonMessages.finish
                                                      )
                                            }
                                            onPress={handleSubmit}
                                            disabled={
                                                !duo.mentor ||
                                                !duo.mentee ||
                                                !isOnline
                                            }
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    </footer>
                )}
            </Dialog>
        </ModalPortal>
    );
};
export default AddDuoModal;

const messages = defineMessages({
    addDuoModalTitle: {
        id: "duoMode-addDuoModal-title",
        defaultMessage: "Add a duo",
    },
    addDuoModalCancel: {
        id: "duoMode-addDuoModal-cancel",
        defaultMessage: "Cancel",
    },
    retry: {
        id: "duoMode-addDuoModal-retry",
        defaultMessage: "Try again",
    },
    error: {
        id: "duoMode-addDuoModal-error",
        defaultMessage: "The duo could not be created. Please try again.",
    },
});
